import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { ModalForm, ModalFormId } from 'src/components/ModalForms/ModalForm'
import { MultiSelectForm } from 'src/components/Reusable/Forms/MultiSelectForm'
import { CheckBoxInput } from 'src/components/Reusable/Inputs/CheckBox'
import { GridColumn } from 'src/components/Reusable/Grid'
import { getPluralization } from 'src/Utils/globalHelpers'
import { NumberSelectedTextPlacement } from 'src/components/ModalForms/ModalForm.styles'
import { StyledInputError } from 'src/components/Reusable/Inputs/TextInput/styles'
import { usePaginateItems } from 'src/components/Reusable/paginate/paginate.hooks'
import { Spinner } from 'src/Throbber'
import { PaginateButtonsGroup } from 'src/components/Reusable/paginate/PaginateButtons'
import {
  MultiSelectFiltersContainer,
  MultiSelectModalRow,
  MultiSelectPaginateContainer,
  MultiSelectStatusContainer,
  MultiSelectStatusText,
} from './MultiSelectModal.styles'

const PAGE_SIZE = 100

/**
 * Selection state for MultiSelectModal. `includeAll` lets a caller
 * represent "everything" without enumerating ids; otherwise `included` holds
 * the explicitly selected ids.
 */
export type MultiSelectState = {
  includeAll: boolean
  included?: Set<string>
}

export type MultiSelectModalItem = {
  id: string
  renderItems: React.ReactNode[]
}

export type MultiSelectModalProps<
  S extends MultiSelectState = MultiSelectState,
> = {
  title: string
  description: string
  itemType: string
  currentSelection: S
  onBack?: () => void
  onCommit: (newSelection: S) => void
  items: MultiSelectModalItem[]
  numberOfColumns: number
  gridTemplateColumnStyling?: string
  show: boolean
  hide: () => void
  isLoading: boolean
  error?: Error | string | null
  filters?: React.ReactNode
  isFilteredResults?: boolean
  modalSize?: 'lg' | 'xl'
  headers?: React.ReactNode
  customActionButton?: React.ReactNode
  formId?: ModalFormId
  submitText?: string
}

export function MultiSelectModal<
  S extends MultiSelectState = MultiSelectState,
>({
  title,
  description,
  itemType,
  currentSelection,
  onBack: originalOnBack,
  onCommit,
  items,
  numberOfColumns,
  gridTemplateColumnStyling,
  show,
  hide: originalHide,
  isLoading,
  error,
  filters,
  isFilteredResults,
  modalSize = 'lg',
  headers,
  customActionButton,
  formId = ModalFormId.CHAT_CONTEXT_ID_SELECTOR,
  submitText,
}: MultiSelectModalProps<S>): JSX.Element {
  const [newSelection, setNewSelection] = useState<S>(currentSelection)

  const { paginatedItems, currentPage, nextPage, prevPage, goToPage } =
    usePaginateItems<MultiSelectModalItem>({
      items,
      pageSize: PAGE_SIZE,
      initialPage: 1,
    })

  useEffect(() => {
    setNewSelection(currentSelection)
    goToPage(1)
  }, [currentSelection, goToPage])

  useEffect(() => {
    goToPage(1)
  }, [items, goToPage])

  const allIds = useMemo(() => items.map((item) => item.id), [items])

  const handleSelectAll = useCallback(
    (e: React.MouseEvent<HTMLElement>) => {
      e.preventDefault()
      e.stopPropagation()
      setNewSelection(
        (prev) =>
          ({
            ...prev,
            includeAll: isFilteredResults ? false : true,
            included: isFilteredResults ? new Set(allIds) : new Set(),
          } as S),
      )
    },
    [isFilteredResults, allIds],
  )

  const handleDeselectAll = useCallback((e: React.MouseEvent<HTMLElement>) => {
    e.preventDefault()
    e.stopPropagation()
    setNewSelection(
      (prev) =>
        ({
          ...prev,
          includeAll: false,
          included: new Set(),
        } as S),
    )
  }, [])

  const reset = useCallback(() => {
    setNewSelection(currentSelection)
  }, [currentSelection])

  const hide = useCallback(() => {
    reset()
    originalHide()
  }, [reset, originalHide])

  const onBack = useCallback(() => {
    reset()
    originalOnBack?.()
  }, [reset, originalOnBack])

  const onSubmit = useCallback(
    (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault()
      e.stopPropagation()
      onCommit(newSelection)
      if (originalOnBack) {
        onBack()
      } else {
        hide()
      }
    },
    [newSelection, onCommit, originalOnBack, onBack, hide],
  )

  const setSelectedItemIds = useCallback(
    (ids: string[]) => {
      const isIncludeAll = ids.length === items.length
      setNewSelection(
        (prev) =>
          ({
            ...prev,
            includeAll: isIncludeAll,
            included: isIncludeAll ? new Set() : new Set(ids),
          } as S),
      )
    },
    [items.length],
  )

  const selectedItemIds = useMemo(() => {
    if (newSelection.includeAll) {
      return allIds
    }
    return Array.from(newSelection.included || [])
  }, [newSelection, allIds])

  const multiSelectFormProps = useMemo(
    () => ({
      formId,
      title: description,
      loading: false,
      gridTemplateColumnStyling: gridTemplateColumnStyling
        ? `min-content ${gridTemplateColumnStyling}`
        : `min-content min-content${' auto'.repeat(numberOfColumns - 1)}`,
      gridItems: items.length ? (
        <>
          {paginatedItems.map((item) => (
            <MultiSelectModalRowItem
              key={item.id}
              item={item}
              setSelectedItemIds={setSelectedItemIds}
              selectedItemIds={selectedItemIds}
            />
          ))}
        </>
      ) : (
        <div />
      ),
      numberSelectedText: `${
        selectedItemIds.length === allIds.length ? 'All ' : ''
      }${selectedItemIds.length} ${getPluralization(
        itemType,
        selectedItemIds.length,
      )} selected`,
      numberSelectedTextPlacement: 'top' as NumberSelectedTextPlacement,
      onSubmit,
    }),
    [
      formId,
      items,
      selectedItemIds,
      description,
      onSubmit,
      itemType,
      setSelectedItemIds,
      allIds.length,
      paginatedItems,
      numberOfColumns,
      gridTemplateColumnStyling,
    ],
  )

  const filtersWithPagination = useMemo(
    () => (
      <MultiSelectFiltersContainer onClick={(e) => void e.preventDefault()}>
        <div style={{ width: '100%' }}>{filters}</div>
        {items.length > 0 && (
          <MultiSelectPaginateContainer>
            <PaginateButtonsGroup
              totalItems={items.length}
              pageSize={PAGE_SIZE}
              useCustomPagesize={false}
              currentPage={currentPage}
              onPrevClick={prevPage}
              onNextClick={nextPage}
            />
          </MultiSelectPaginateContainer>
        )}
      </MultiSelectFiltersContainer>
    ),
    [filters, currentPage, prevPage, nextPage, items.length],
  )

  // Only reserve space for a status row when there's actually something to show.
  const footer = useMemo(() => {
    const status = error ? (
      <StyledInputError showError={!!error}>
        {typeof error === 'object' && 'message' in error
          ? error.message
          : error}
      </StyledInputError>
    ) : isLoading ? (
      <Spinner size="m" color="primary" />
    ) : !items.length ? (
      <MultiSelectStatusText>No data found</MultiSelectStatusText>
    ) : null
    return status ? (
      <MultiSelectStatusContainer>{status}</MultiSelectStatusContainer>
    ) : undefined
  }, [error, isLoading, items.length])

  const handleFormKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      // Prevent the form from being submitted when Enter is pressed inside an input (e.g. the search box).
      if (e.key === 'Enter' && (e.target as HTMLElement).tagName === 'INPUT') {
        e.preventDefault()
      }
    },
    [],
  )

  return (
    <ModalForm
      show={show}
      hide={hide}
      formId={formId}
      size={modalSize}
      title={title}
      submitText={submitText ?? (originalOnBack ? 'Continue' : 'Confirm')}
      showOverflow
      disableSubmitButton={!items.length}
      hideBack={!originalOnBack}
      onBack={onBack}
      customActionButton={customActionButton}
    >
      <div onKeyDown={handleFormKeyDown}>
        <MultiSelectForm
          {...multiSelectFormProps}
          showSelectButtons
          handleDeselectAll={handleDeselectAll}
          handleSelectAll={handleSelectAll}
          gridRowGap={5}
          gridAutoFlow="row"
          filters={filtersWithPagination}
          headers={headers}
          footer={footer}
        />
      </div>
    </ModalForm>
  )
}

type MultiSelectModalRowItemProps = {
  item: MultiSelectModalItem
  setSelectedItemIds: (ids: string[]) => void
  selectedItemIds: string[]
}

const MultiSelectModalRowItem = ({
  item,
  setSelectedItemIds,
  selectedItemIds,
}: MultiSelectModalRowItemProps) => {
  const data = useMemo(() => new Set(selectedItemIds), [selectedItemIds])

  const handleChange = useCallback(
    (
      e: React.MouseEvent<HTMLDivElement> | React.ChangeEvent<HTMLInputElement>,
    ) => {
      e.stopPropagation()
      const newData = new Set(data)
      if (data.has(item.id)) {
        newData.delete(item.id)
      } else {
        newData.add(item.id)
      }
      setSelectedItemIds(Array.from(newData))
    },
    [item.id, setSelectedItemIds, data],
  )

  return (
    <MultiSelectModalRow
      id={`select-multi-select-item-${item.id}`}
      onClick={handleChange}
    >
      <GridColumn>
        <CheckBoxInput id={item.id} checked={data.has(item.id)} />
      </GridColumn>
      {item.renderItems.map((renderItem, index) => (
        <GridColumn minHeight={59} key={index}>
          {renderItem}
        </GridColumn>
      ))}
    </MultiSelectModalRow>
  )
}
